import { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { apiClient } from '@/lib/api';

interface DeleteTestDialogProps {
  open: boolean;
  testId: string;
  onClose: () => void;
  onSuccess: () => void;
  onError: (message: string) => void;
}

export function DeleteTestDialog({
  open,
  testId,
  onClose,
  onSuccess,
  onError,
}: DeleteTestDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await apiClient.delete(`/api/v1/tests/${testId}`);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? 'Failed to delete test';
      onError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={isDeleting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Test?</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mt: 1 }}>
          Are you sure you want to delete this test?
          <br />
          <br />
          <strong>This action cannot be undone.</strong> The test will be
          permanently removed from all views and reports. A deletion record will
          be kept for audit purposes.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          color="error"
          variant="contained"
          disabled={isDeleting}
          startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isDeleting ? 'Deleting…' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
